var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef, } from 'react';
import { uniqueId } from '../../util/id';
import { RadioButton, RadioButtonGroupContext, } from '../RadioButton/RadioButton';
import { FieldLabelText, FieldValidationHint, FieldLegend, FieldSet, } from '../FieldAtoms';
import { AccessibilityError } from '../../util/errors';
import { isEmpty } from '../../util/helpers';
/**
 * A group of RadioButtons.
 */
export const RadioButtonGroup = forwardRef((_a, ref) => {
    var { options, onChange, onBlur, value, defaultValue, 'name': customName, label, invalid, validationHint, showValid, disabled, hasWarning, hideLabel, optionalLabel, required, 'aria-describedby': descriptionId } = _a, props = __rest(_a, ["options", "onChange", "onBlur", "value", "defaultValue", 'name', "label", "invalid", "validationHint", "showValid", "disabled", "hasWarning", "hideLabel", "optionalLabel", "required", 'aria-describedby']);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label) {
        throw new AccessibilityError('RadioButtonGroup', 'The `label` prop is missing. Pass `hideLabel` if you intend to hide the label visually.');
    }
    if (isEmpty(options)) {
        return null;
    }
    const name = customName || uniqueId('radio-button-group_');
    const validationHintId = uniqueId('validation-hint_');
    const descriptionIds = `${descriptionId ? `${descriptionId} ` : ''}${validationHintId}`;
    return (_jsxs(FieldSet, Object.assign({ role: "radiogroup", "aria-describedby": descriptionIds, "aria-orientation": "vertical", name: name, ref: ref, disabled: disabled }, props, { children: [_jsx(FieldLegend, { children: _jsx(FieldLabelText, { label: label, hideLabel: hideLabel, optionalLabel: optionalLabel, required: required }) }), _jsx(RadioButtonGroupContext.Provider, Object.assign({ value: true }, { children: options.map((_a) => {
                    var { className, style } = _a, option = __rest(_a, ["className", "style"]);
                    return (_jsx("div", Object.assign({ className: className, style: style }, { children: _jsx(RadioButton, Object.assign({}, option, { name: name, onChange: onChange, onBlur: onBlur, required: required, disabled: disabled || option.disabled, invalid: invalid || option.invalid, checked: value ? option.value === value : option.checked, defaultChecked: defaultValue
                                ? option.value === defaultValue
                                : option.defaultChecked })) }), option.label));
                }) })), _jsx(FieldValidationHint, { id: validationHintId, invalid: invalid, showValid: showValid, disabled: disabled, hasWarning: hasWarning, validationHint: validationHint })] })));
});
RadioButtonGroup.displayName = 'RadioButtonGroup';
